"use client";

import React, {useEffect} from "react";
import Image from "next/image";
import {Locale} from "~/i18n-config";
import {usePathname} from "next/navigation";
import {useClientAuth} from "~/components/providers/client/AuthProvider";
import {CloseContextProvider} from "~/components/providers/client/CloseContextProvider";
import {AnimatePresence, motion} from "framer-motion";
import {Popover} from "@headlessui/react";
import {SearchParamsBoundary} from "@/components/navigation/SearchParamsBoundary";
import clsx from "clsx";
import {LangLink} from "~/components/locales/LangLink";

function HeaderBarContent(
    { lang, homeUrl, main, mainMobile, notification, account, accountMobile, className, searchParams } :
        {
            lang: Locale,
            homeUrl: string,
            main: React.ReactNode,
            mainMobile: React.ReactNode,
            notification?: React.ReactNode,
            account: React.ReactNode,
            accountMobile: React.ReactNode,
            searchParams: URLSearchParams,
            className?: string
        }
) {

    const pathname = usePathname();

    const showNavigation = !pathname.includes("/signup/");

    const { user } = useClientAuth();

    const companyParam = searchParams.get("company");
    const assetParam = searchParams.get("asset");

    let homeUrlWithSearchParams = homeUrl;

    if (companyParam || assetParam) {
        const params = new URLSearchParams();
        if (companyParam) params.append("company", companyParam);
        if (assetParam) params.append("asset", assetParam);
        homeUrlWithSearchParams = `${homeUrl}?${params.toString()}`;
    }

    return (
        <header className={clsx("px-2", className)}>
            <nav>
                <div className="mx-auto max-w-7xl flex justify-between px-4 py-6">
                    <div className="flex items-center gap-20">
                        <div className="grow-0">
                            <LangLink className="relative block w-[164px] h-[40px]" lang={lang} href={homeUrlWithSearchParams}>
                                <Image priority={true} fill={true} alt="DocID" src="/images/logo-docid-white.svg" />
                            </LangLink>
                        </div>
                        {showNavigation && main}
                    </div>
                    <div className="flex items-center gap-4">
                        {notification && (
                            <div className="flex items-center">
                                {notification}
                            </div>
                        )}
                        <Popover className="lg:hidden">
                            {({ open, close }) => {

                                useEffect(() => {
                                    close();
                                }, [pathname]);

                                return (
                                    <CloseContextProvider closeAction={() => close()}>
                                        <Popover.Button className="relative z-1001! inline-flex size-[50px] outline-hidden" aria-label="Open navigation">
                                            <div className="relative">
                                                <div className="relative flex overflow-hidden items-center justify-center rounded-full w-[50px] h-[50px] transform transition-all bg-zinc-800 ring-0 hover:ring-4 ring-white/60 duration-200 shadow-md">
                                                    <div
                                                        aria-hidden="true"
                                                        className="relative flex flex-col justify-between w-[20px] h-[20px] transform transition-all duration-300 origin-center overflow-hidden">
                                                        <div className={clsx("bg-white h-[2px] w-7 transform transition-all duration-300 origin-left", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx("bg-white h-[2px] w-7 rounded-sm transform transition-all duration-300 delay-75", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx("bg-white h-[2px] w-7 transform transition-all duration-300 origin-left delay-150", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx(
                                                            "absolute inset-0 flex items-center justify-center transform transition-all duration-300",
                                                            open ? "scale-100 opacity-100 delay-300" : "scale-75 opacity-0",
                                                        )}>
                                                            <div className="absolute bg-white h-[2px] w-5 rotate-45"></div>
                                                            <div className="absolute bg-white h-[2px] w-5 -rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Popover.Button>
                                        <AnimatePresence initial={false}>
                                            {open && (
                                                <>
                                                    <Popover.Overlay
                                                        static
                                                        as={motion.div}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        className="fixed inset-0 z-998! bg-white/20 backdrop-blur-sm"
                                                    />
                                                    <Popover.Panel
                                                        static
                                                        as={motion.div}
                                                        initial={{ opacity: 0, y: -20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{
                                                            opacity: 0,
                                                            y: -20,
                                                            transition: { duration: 0.2 },
                                                        }}
                                                        className="absolute inset-x-0 top-0 z-999! origin-top pt-5 rounded-b-2xl bg-white shadow-2xl shadow-zinc-900/20">
                                                        <LangLink href={(user) ? "/dashboard" : "/"} aria-label="DocID" className="block mx-6" lang={lang}>
                                                            <Image src="/images/logo-docid.svg" width={160} height={34} alt="DocID" />
                                                        </LangLink>
                                                        {mainMobile}
                                                        {accountMobile}
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </AnimatePresence>
                                    </CloseContextProvider>
                                )
                            }}
                        </Popover>
                        {account}
                    </div>
                </div>
            </nav>
        </header>
    )
}

export function HeaderBar(props: Omit<React.ComponentProps<typeof HeaderBarContent>, "searchParams">) {
    return <SearchParamsBoundary>{searchParams => <HeaderBarContent {...props} searchParams={searchParams}/>}</SearchParamsBoundary>;
}
